// Developer: Ankit Gupta
/**
 * The planned-downtime banner (24 Oct 2026): a slim strip above the top bar that warns before a
 * maintenance window and says it is under way until it ends. Platform administrators keep the
 * windows under Admin > Maintenance; this reads what to say from GET /public/maintenance each
 * minute and counts down by the server's clock. A warning can be closed for that window; the
 * notice that it is under way cannot. Times are given in the window's own zone (KSA by default),
 * so everyone reads the same 2:00 PM.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CloseOutlined, ToolOutlined } from '@ant-design/icons';
import { api } from '@/api/client';
import { useLanguage, useT } from '@/i18n';
import { browserOrUiLocale } from '@/utils/format';

interface Notice {
  id: number;
  state: 'upcoming' | 'active';
  starts_at: string;
  ends_at: string;
  timezone: string;
  message: string | null;
  message_ar: string | null;
}

const CLOSED_KEY = 'datapilot.maintenance-closed';

function closedFor(): string | null {
  try { return window.localStorage.getItem(CLOSED_KEY); } catch { return null; }
}

export function MaintenanceBanner() {
  const { t } = useT('shell');
  const lang = useLanguage();
  const q = useQuery({
    queryKey: ['maintenance'],
    queryFn: async () => {
      const r = await api.get<{ notice: Notice | null; server_time: string }>('/public/maintenance');
      return { ...r, skew: new Date(r.server_time).getTime() - Date.now() };
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });
  // the clock the banner counts by: the server's, ticking here between reads
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const h = window.setInterval(() => setNow(Date.now()), 15_000); return () => window.clearInterval(h); }, []);
  const [closed, setClosed] = useState(closedFor);
  const n = q.data?.notice;
  const at = now + (q.data?.skew ?? 0);
  const starts = n ? new Date(n.starts_at).getTime() : 0;
  const ends = n ? new Date(n.ends_at).getTime() : 0;
  const state = !n || at >= ends ? null : at >= starts ? 'active' : 'upcoming';
  const key = n ? `${n.id}@${n.starts_at}` : '';
  const shown = !!state && !(state === 'upcoming' && closed === key);

  // the Orion screen fills the window below the top bar; it makes room for the strip, however
  // many lines it wraps to on a narrow screen
  const strip = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = document.documentElement.style;
    const el = strip.current;
    if (!shown || !el) { root.setProperty('--dp-banner-h', '0px'); return; }
    const measure = () => root.setProperty('--dp-banner-h', `${el.offsetHeight}px`);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => { ro.disconnect(); root.setProperty('--dp-banner-h', '0px'); };
  }, [shown]);

  const text = useMemo(() => {
    if (!n || !state) return '';
    const own = (lang === 'ar' ? n.message_ar : null) ?? n.message;
    const clock = (ms: number) => new Date(ms).toLocaleTimeString(browserOrUiLocale(), { hour: 'numeric', minute: '2-digit', timeZone: n.timezone });
    const day = (ms: number) => new Date(ms).toLocaleDateString('en-CA', { timeZone: n.timezone });
    const zone = n.timezone === 'Asia/Riyadh' ? t('maintenance.ksa') : n.timezone;
    const when = day(starts) === day(at) ? t('maintenance.today') : new Date(starts).toLocaleDateString(browserOrUiLocale(), { weekday: 'long', day: 'numeric', month: 'short', timeZone: n.timezone });
    const vars = { from: clock(starts), to: clock(ends), zone, when, minutes: Math.round((ends - starts) / 60_000) };
    const lead = state === 'active' ? t('maintenance.active', vars) : t('maintenance.upcoming', vars);
    if (own) return `${lead} ${own}`;
    if (state === 'upcoming') {
      const left = Math.max(1, Math.ceil((starts - at) / 60_000));
      return `${lead} ${t('maintenance.upcomingMore', { count: left, minutes: vars.minutes })}`;
    }
    return `${lead} ${t('maintenance.activeMore')}`;
  }, [n, state, lang, t, starts, ends, at]);

  if (!shown) return null;
  return (
    <div ref={strip} className={`dp-maintenance is-${state}`} role={state === 'active' ? 'alert' : 'status'}>
      <ToolOutlined aria-hidden="true" />
      <span className="dp-maintenance-text">{text}</span>
      {state === 'upcoming' && (
        <button type="button" className="dp-maintenance-close" aria-label={t('maintenance.close')}
          onClick={() => { try { window.localStorage.setItem(CLOSED_KEY, key); } catch { /* storage is a convenience */ } setClosed(key); }}>
          <CloseOutlined />
        </button>
      )}
    </div>
  );
}
